import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Users, Mail, Phone, Clock, Search, MessageSquare, StickyNote, Bell,
  Plus, X as XIcon, QrCode, CheckCircle2, ShoppingCart, Globe, AlertCircle,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

const STATUS_MAP = {
  new:               { label: "Nuevo",            color: "bg-blue-100 text-blue-700 border-blue-200" },
  contacted:         { label: "Contactado",        color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  qualified:         { label: "Cualificado",       color: "bg-purple-100 text-purple-700 border-purple-200" },
  abandoned_cart:    { label: "Carrito abandon.",  color: "bg-orange-100 text-orange-700 border-orange-200" },
  checkout_started:  { label: "Checkout inic.",    color: "bg-amber-100 text-amber-700 border-amber-200" },
  converted:         { label: "Convertido",        color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  lost:              { label: "Perdido",           color: "bg-gray-100 text-gray-500 border-gray-200" },
};

const SOURCE_MAP = {
  carol_widget:      { label: "Chat/Site",    icon: Globe,        color: "bg-blue-50 text-blue-600" },
  dine_in_qr:        { label: "QR Mesa",      icon: QrCode,       color: "bg-teal-50 text-teal-700" },
  abandoned_cart:    { label: "Carr. aband.", icon: ShoppingCart, color: "bg-orange-50 text-orange-700" },
  checkout_started:  { label: "Checkout",     icon: ShoppingCart, color: "bg-amber-50 text-amber-700" },
  whatsapp:          { label: "WhatsApp",     icon: MessageSquare,color: "bg-green-50 text-green-700" },
  website:           { label: "Site",         icon: Globe,        color: "bg-indigo-50 text-indigo-700" },
  manual:            { label: "Manual",       icon: Users,        color: "bg-gray-50 text-gray-600" },
};

const SOURCE_FILTERS = [
  { key: "all",           label: "Todos" },
  { key: "carol_widget",  label: "Chat/Site" },
  { key: "dine_in_qr",   label: "QR Mesa" },
  { key: "abandoned_cart",label: "Carrito aband." },
  { key: "checkout_started",label: "Checkout" },
  { key: "converted",     label: "Convertidos", isStatus: true },
  { key: "lost",          label: "Perdidos",    isStatus: true },
];

function SourceBadge({ source }) {
  const cfg = SOURCE_MAP[source] || { label: source || "?", icon: Users, color: "bg-gray-50 text-gray-500" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

function QRLeadsTab({ token }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/v1/restaurant-leads?source=dine_in_qr`, { headers });
      setLeads(res.data || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,"0")}/${String(dt.getMonth()+1).padStart(2,"0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
  };

  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.name || "").toLowerCase().includes(q) || (l.phone || "").toLowerCase().includes(q);
  });

  const stats = {
    total: leads.length,
    consented: leads.filter(l => l.marketing_consent).length,
    converted: leads.filter(l => l.converted_to_order).length,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: "Total", value: stats.total, icon: Users, color: "text-gray-700" },
          { label: "Com consentimento", value: stats.consented, icon: CheckCircle2, color: "text-teal-600" },
          { label: "Fizeram pedido", value: stats.converted, icon: MessageSquare, color: "text-green-600" },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xl font-bold">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nome ou telefone..."
          className="pl-10"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-400">A carregar...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <QrCode className="w-8 h-8 mx-auto mb-2 opacity-30" />
              {leads.length === 0
                ? "Nenhum cliente capturado via QR de mesa ainda. Os clientes aparecem aqui quando identificam nome e telefone na mesa."
                : "Nenhum resultado para esse filtro."}
            </div>
          ) : (
            <>
              <div className="sm:hidden divide-y">
                {filtered.map(lead => (
                  <div key={lead.id} className="p-3 space-y-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-sm text-gray-900 truncate">{lead.name || "-"}</p>
                      <span className="text-xs text-gray-500 shrink-0">{lead.table_number ? `Mesa ${lead.table_number}` : ""}</span>
                    </div>
                    <p className="text-xs text-gray-600 flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" />{lead.phone || "-"}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {lead.marketing_consent
                        ? <Badge className="bg-teal-100 text-teal-700 border-teal-200 text-xs">Consent. ✓</Badge>
                        : <Badge className="bg-gray-100 text-gray-500 border-gray-200 text-xs">Sem consent.</Badge>}
                      {lead.converted_to_order && <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Fez pedido</Badge>}
                      <span className="text-xs text-gray-400 flex items-center gap-0.5 ml-auto"><Clock className="w-3 h-3" />{formatDate(lead.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Nome</th>
                      <th className="px-4 py-3">Telefone</th>
                      <th className="px-4 py-3">Mesa</th>
                      <th className="px-4 py-3">Consentimento</th>
                      <th className="px-4 py-3">Pedido</th>
                      <th className="px-4 py-3">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(lead => (
                      <tr key={lead.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3"><p className="font-medium text-sm text-gray-900">{lead.name || "-"}</p></td>
                        <td className="px-4 py-3"><p className="text-sm text-gray-600 flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone || "-"}</p></td>
                        <td className="px-4 py-3"><span className="text-sm text-gray-600">{lead.table_number ? `Mesa ${lead.table_number}` : "-"}</span></td>
                        <td className="px-4 py-3">
                          {lead.marketing_consent
                            ? <Badge className="bg-teal-100 text-teal-700 border-teal-200 text-xs">Sim</Badge>
                            : <Badge className="bg-gray-100 text-gray-500 border-gray-200 text-xs">Não</Badge>}
                        </td>
                        <td className="px-4 py-3">
                          {lead.converted_to_order
                            ? <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Sim</Badge>
                            : <Badge className="bg-gray-100 text-gray-500 border-gray-200 text-xs">Não</Badge>}
                        </td>
                        <td className="px-4 py-3"><p className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(lead.created_at)}</p></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 text-center">
        Clientes com consentimento aparecem como segmento "QR Mesa (c/ consentimento)" em Campanhas IA.
      </p>
    </div>
  );
}

function AllLeadsTab({ token }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v1/restaurant-leads?source=!dine_in_qr`, { headers });
      setLeads(res.data || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateLead = async (id, updates) => {
    await axios.patch(`${API}/api/v1/restaurant-leads/${id}`, updates, { headers });
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
    if (selectedLead?.id === id) setSelectedLead(prev => ({ ...prev, ...updates }));
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,"0")}/${String(dt.getMonth()+1).padStart(2,"0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
  };

  const filtered = leads.filter(l => {
    const f = SOURCE_FILTERS.find(sf => sf.key === sourceFilter);
    if (f && sourceFilter !== "all") {
      if (f.isStatus) {
        if (l.status !== sourceFilter) return false;
      } else {
        if (l.source !== sourceFilter) return false;
      }
    }
    if (search) {
      const q = search.toLowerCase();
      return (l.name || "").toLowerCase().includes(q) ||
             (l.phone || "").toLowerCase().includes(q) ||
             (l.source || "").toLowerCase().includes(q);
    }
    return true;
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === "new").length,
    abandoned: leads.filter(l => l.status === "abandoned_cart" || l.source === "abandoned_cart").length,
    converted: leads.filter(l => l.status === "converted").length,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total",           value: stats.total,     icon: Users,        color: "text-gray-700" },
          { label: "Novos",           value: stats.new,       icon: MessageSquare,color: "text-blue-600" },
          { label: "Carr. abandon.",  value: stats.abandoned, icon: ShoppingCart, color: "text-orange-600" },
          { label: "Convertidos",     value: stats.converted, icon: CheckCircle2, color: "text-green-600" },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xl font-bold">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            className="pl-10"
            data-testid="leads-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {SOURCE_FILTERS.filter(f => f.key === "all" || leads.some(l => f.isStatus ? l.status === f.key : l.source === f.key)).map(f => (
            <button
              key={f.key}
              onClick={() => setSourceFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-all ${
                sourceFilter === f.key
                  ? "bg-[#CDA34F] text-white border-[#CDA34F]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`filter-source-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-400">A carregar...</div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-gray-400" data-testid="no-leads">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="font-medium text-gray-500 mb-1">Nenhum lead capturado ainda</p>
              <p className="text-sm max-w-sm mx-auto">
                Os leads aparecem aqui quando um cliente identifica o telefone via chat do site,
                adiciona produtos ao carrinho ou inicia o checkout.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Nenhum resultado para esse filtro.</div>
          ) : (
            <>
              <div className="sm:hidden divide-y" data-testid="leads-table">
                {filtered.map(lead => (
                  <div key={lead.id} className="p-3 space-y-1.5" data-testid={`lead-row-${lead.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-gray-900 truncate">{lead.name || <span className="text-gray-400 italic">Sem nome</span>}</p>
                        {lead.phone && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3 shrink-0" />{lead.phone}</p>}
                      </div>
                      <select
                        value={lead.status || "new"}
                        onChange={e => updateLead(lead.id, { status: e.target.value })}
                        className={`text-xs font-medium px-2 py-0.5 rounded-lg border cursor-pointer shrink-0 ${STATUS_MAP[lead.status]?.color || "bg-gray-100 text-gray-500"}`}
                        data-testid={`lead-status-${lead.id}`}
                      >
                        {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <SourceBadge source={lead.source} />
                      {lead.last_cart_total != null && (
                        <span className="text-xs font-semibold text-orange-700">{Number(lead.last_cart_total).toFixed(2)} €</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-400 flex items-center gap-0.5"><Clock className="w-3 h-3" />{formatDate(lead.created_at)}</span>
                      <button
                        onClick={() => { setSelectedLead(lead); setNotes(lead.notes || ""); }}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        data-testid={`lead-detail-${lead.id}`}
                      >
                        <StickyNote className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full" data-testid="leads-table-desktop">
                  <thead>
                    <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Nome / Telefone</th>
                      <th className="px-4 py-3">Origem</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3">Carrinho</th>
                      <th className="px-4 py-3">Data</th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(lead => (
                      <tr key={lead.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-sm text-gray-900">{lead.name || <span className="text-gray-400 italic">Sem nome</span>}</p>
                          {lead.phone && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" />{lead.phone}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3"><SourceBadge source={lead.source} /></td>
                        <td className="px-4 py-3">
                          <select
                            value={lead.status || "new"}
                            onChange={e => updateLead(lead.id, { status: e.target.value })}
                            className={`text-xs font-medium px-2 py-1 rounded-lg border cursor-pointer ${STATUS_MAP[lead.status]?.color || "bg-gray-100 text-gray-500"}`}
                            data-testid={`lead-status-${lead.id}`}
                          >
                            {Object.entries(STATUS_MAP).map(([k, v]) => (
                              <option key={k} value={k}>{v.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          {lead.last_cart_total != null ? (
                            <div>
                              <p className="text-sm font-semibold text-orange-700">{Number(lead.last_cart_total).toFixed(2)} €</p>
                              {lead.last_products?.length > 0 && (
                                <p className="text-xs text-gray-400 truncate max-w-[140px]">
                                  {lead.last_products.map(p => p.name || "").filter(Boolean).join(", ")}
                                </p>
                              )}
                            </div>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(lead.created_at)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => { setSelectedLead(lead); setNotes(lead.notes || ""); }}
                            className="text-gray-400 hover:text-gray-700 transition-colors"
                            data-testid={`lead-detail-${lead.id}`}
                          >
                            <StickyNote className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-md" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detalhe do Lead</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Nome:</span> <strong>{selectedLead.name || "-"}</strong></div>
                <div><span className="text-gray-500">Estado:</span> <Badge className={STATUS_MAP[selectedLead.status]?.color}>{STATUS_MAP[selectedLead.status]?.label || selectedLead.status}</Badge></div>
                {selectedLead.phone && <div className="col-span-2"><span className="text-gray-500">Telefone:</span> <strong>{selectedLead.phone}</strong></div>}
                <div className="col-span-2"><span className="text-gray-500">Origem:</span> <SourceBadge source={selectedLead.source} /></div>
                {selectedLead.last_cart_total != null && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Carrinho:</span>{" "}
                    <strong className="text-orange-700">{Number(selectedLead.last_cart_total).toFixed(2)} €</strong>
                    {selectedLead.last_products?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedLead.last_products.map(p => p.name).filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                )}
                <div className="col-span-2"><span className="text-gray-500">Data:</span> {formatDate(selectedLead.created_at)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Notas</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  className="w-full text-sm px-3 py-2 border rounded-lg focus:outline-none focus:border-[#CDA34F]"
                  placeholder="Adicionar notas sobre este lead..."
                  data-testid="lead-notes-input"
                />
              </div>
              <Button
                onClick={() => { updateLead(selectedLead.id, { notes }); setSelectedLead(null); }}
                className="w-full bg-[#CDA34F] hover:bg-[#b8903e]"
                data-testid="lead-save-notes"
              >
                Guardar Notas
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotifySettingsPanel({ token }) {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/v1/restaurant-leads/notify-settings`, { headers })
      .then(r => setEmails(r.data?.emails || []))
      .catch(() => {});
  }, [token]);

  const add = async () => {
    const e = newEmail.trim();
    if (!e || !e.includes("@") || emails.includes(e)) return;
    const updated = [...emails, e];
    await axios.put(`${API}/api/v1/restaurant-leads/notify-settings`, { emails: updated }, { headers });
    setEmails(updated);
    setNewEmail("");
  };

  const remove = async (email) => {
    const updated = emails.filter(e => e !== email);
    await axios.put(`${API}/api/v1/restaurant-leads/notify-settings`, { emails: updated }, { headers });
    setEmails(updated);
  };

  return (
    <Card data-testid="notify-settings-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#CDA34F]" />
          <p className="text-sm font-semibold text-gray-700">Emails para receber notificações de novos leads</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {emails.map(email => (
            <span key={email} className="inline-flex items-center gap-1.5 bg-gray-100 text-sm px-3 py-1.5 rounded-full" data-testid={`notify-email-${email}`}>
              {email}
              <button onClick={() => remove(email)} className="text-gray-400 hover:text-red-500 transition-colors">
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {emails.length === 0 && <span className="text-sm text-gray-400">Nenhum email configurado. Será usado o email do admin.</span>}
        </div>
        <div className="flex gap-2">
          <Input
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="novo@email.com"
            className="flex-1"
            onKeyDown={e => e.key === "Enter" && add()}
            data-testid="notify-new-email-input"
          />
          <Button onClick={add} size="sm" className="bg-[#CDA34F] hover:bg-[#b8903e]" data-testid="notify-add-email-btn">
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function LeadsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [showNotify, setShowNotify] = useState(false);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="leads-title">Leads & Clientes</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Contactos capturados via chat do site, QR de mesa e carrinho abandonado</p>
        </div>
        <Button
          variant={showNotify ? "default" : "outline"}
          size="sm"
          onClick={() => setShowNotify(!showNotify)}
          className={`w-full sm:w-auto ${showNotify ? "bg-[#CDA34F] hover:bg-[#b8903e]" : ""}`}
          data-testid="notify-settings-btn"
        >
          <Bell className="w-4 h-4 mr-1.5" />
          Notificações
        </Button>
      </div>

      {showNotify && <NotifySettingsPanel token={token} />}

      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab("all")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "all" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
          data-testid="tab-all"
        >
          <Globe className="w-4 h-4" /> Leads Site / Chat
        </button>
        <button
          onClick={() => setActiveTab("qr")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "qr" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
          data-testid="tab-qr"
        >
          <QrCode className="w-4 h-4" /> Clientes QR Mesa
        </button>
      </div>

      {activeTab === "all" && <AllLeadsTab token={token} />}
      {activeTab === "qr"  && <QRLeadsTab  token={token} />}
    </div>
  );
}
